/**
 * Как обучение понимает, что шаг пройден, и какую кнопку ему нажать самому.
 *
 * Вынесено из BoardTour отдельно, потому что ошибались тут трижды подряд, и
 * каждый раз это выглядело одинаково: шаг «не засчитывается» или карточка
 * прыгает по кругу. Проверками закрыт каждый случай.
 */

type SceneLike = Record<string, unknown>;

/** Сколько на доске надписей, в которых что-то написано. */
export function filledTextCount(objects: SceneLike[]): number {
  return objects.filter((o) => {
    if (String(o.type) !== "text") return false;
    const t = typeof o.text === "string" ? o.text : "";
    return t.trim().length > 0;
  }).length;
}

/**
 * Отпечаток калькуляторов — меняется от нажатия любой кнопки.
 *
 * Шаг «посчитайте на нём» раньше ждал, что калькуляторов станет больше,
 * хотя калькулятор ставили шагом раньше. Пройти его можно было только
 * поставив второй.
 */
export function calcSignature(objects: SceneLike[]): string {
  return objects
    .filter((o) => String(o.type) === "calculator")
    .map((o) => `${String(o.id)}:${String(o.expr ?? "")}:${String(o.display ?? "")}`)
    .join("|");
}

/** Все типы, которые получаются инструментом «Фигуры». */
export const TOUR_SHAPE_TYPES = [
  "rect",
  "ellipse",
  "triangle",
  "diamond",
  "star",
  // Второй ряд набора: шестиугольник, трапеция, цилиндр и прочие — все они
  // приходят одним типом geom. Без него половина фигур не засчитывалась.
  "geom",
  "line",
  "arrow",
] as const;

/**
 * Годится ли элемент в цель шага.
 *
 * Прежняя проверка была `offsetParent !== null`, и она врёт про элементы с
 * position: fixed — у них offsetParent пуст ВСЕГДА. Так из виду пропадало
 * окошко выбора цвета: условие «окошко уже открыто» не выполнялось никогда,
 * и шаг каждую секунду пытался открыть его заново.
 */
export function isUsableTarget(el: Element | null | undefined): el is HTMLElement {
  if (!(el instanceof HTMLElement)) return false;
  const view = el.ownerDocument?.defaultView;
  const style = view ? view.getComputedStyle(el) : null;
  if (style && (style.display === "none" || style.visibility === "hidden")) return false;
  // У fixed offsetParent пуст всегда — для него отдельная ветка.
  if (style && style.position === "fixed") return true;
  return el.offsetParent != null;
}

export type OpenerCandidate = { expanded: boolean };

/**
 * Какое звено цепочки надо нажать, чтобы добраться до цели.
 *
 * Правило одно: нажимаем самое глубокое ДОСТУПНОЕ и ещё не раскрытое звено.
 * Если глубокое уже раскрыто — не нажимаем НИЧЕГО. Раньше в этом случае
 * выбор молча откатывался на предыдущее звено, а предыдущее — это кнопка
 * инструмента, и она закрывала всю панель вместе с палитрой. Шаг ходил по
 * кругу: закрыли панель, открыли, открыли окошко, снова закрыли панель.
 */
export function pickOpenerIndex(chain: (OpenerCandidate | null)[]): number | null {
  let pick: number | null = null;
  for (let i = 0; i < chain.length; i++) {
    const link = chain[i];
    if (!link) continue;
    pick = link.expanded ? null : i;
  }
  return pick;
}

/** Раздел обучения: название и шаг, с которого он начинается. */
export type TourChapterMark = { title: string; from: string };
export type TourChapter = { title: string; start: number };

/**
 * Разделы для маршрута, который сейчас идёт.
 *
 * Короткий обход берёт не все шаги, поэтому раздел, от которого в нём
 * ничего не осталось, в список не попадает: иначе в оглавлении висели бы
 * названия, ведущие в никуда.
 */
export function chaptersOf(ids: string[], marks: TourChapterMark[]): TourChapter[] {
  const out: TourChapter[] = [];
  for (const m of marks) {
    const start = ids.indexOf(m.from);
    if (start < 0) continue;
    const prev = out[out.length - 1];
    if (prev && prev.start === start) continue;
    out.push({ title: m.title, start });
  }
  // Первый раздел всегда с самого начала, иначе первые шаги остаются без
  // названия и полоска показывает пустое место.
  const first = out[0];
  if (first && first.start > 0) out[0] = { title: first.title, start: 0 };
  return out;
}

/** В каком разделе находится шаг. */
export function chapterAt(chapters: TourChapter[], index: number): TourChapter | null {
  let found: TourChapter | null = null;
  for (const c of chapters) {
    if (c.start <= index) found = c;
  }
  return found;
}

/** Насколько пройдено — для полоски. Последний шаг даёт ровно сто. */
export function progressPercent(index: number, total: number): number {
  if (total <= 0) return 0;
  const p = ((index + 1) / total) * 100;
  return Math.max(0, Math.min(100, Math.round(p)));
}

/**
 * С какого шага продолжить. Шага могло и не остаться: обучение закрыли на
 * полном обходе, а вернулись в короткий — тогда начинаем сначала.
 */
export function resumeIndex(ids: string[], savedId: string | null): number {
  if (!savedId) return 0;
  const i = ids.indexOf(savedId);
  return i < 0 ? 0 : i;
}
